import { addDays, iso, parseIso, utc } from './marketCalendar';

/**
 * The weekdays that make up a month's grid, in Monday-to-Friday rows.
 *
 * Built a **week at a time from a Monday**, so `index % 5` is the weekday
 * column by construction. Walking day by day and skipping weekends does not
 * guarantee that: a month whose 1st falls on a Saturday or Sunday starts the
 * walk mid-week, and every cell in the month then renders one column to the
 * left of the weekday it belongs to. August 2026 opens on a Saturday and put
 * Wednesday's VIX settlement under the Tue header.
 *
 * Padding days belong to neighbouring months and are dimmed by the caller
 * rather than left blank — a half-empty first row reads as missing data.
 *
 * Lives outside `CalendarPage.tsx` so the page exports only its component:
 * a non-component export from a `.tsx` file disables React Fast Refresh for
 * the whole module.
 */
export function monthGrid(year: number, month: number): string[] {
  const first = utc(year, month, 1);
  const dow = first.getUTCDay();

  // The Monday whose week the 1st falls in. A month opening at the weekend has
  // no weekday cells in that week at all, so it begins the Monday after.
  const firstMonday =
    dow === 0 ? addDays(first, 1) // Sunday
      : dow === 6 ? addDays(first, 2) // Saturday
        : addDays(first, -(dow - 1)); // Mon–Fri: back to that week's Monday

  const days: string[] = [];
  // Six weeks covers every month layout; trailing all-outside weeks are
  // dropped below.
  for (let w = 0; w < 6; w++) {
    for (let i = 0; i < 5; i++) days.push(iso(addDays(firstMonday, w * 7 + i)));
  }

  // Trim whole trailing weeks that fall entirely outside the month.
  while (days.length > 5) {
    const lastWeek = days.slice(-5);
    if (lastWeek.every((d) => parseIso(d).getUTCMonth() !== month)) days.length -= 5;
    else break;
  }
  return days;
}
